import React, { useEffect, useRef, useState } from 'react';
import { loggingService } from '../../../services/logging/loggingService';
import { useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.heat';

interface HeatmapPoint {
  lat: number;
  lng: number;
  value: number;
}

interface HeatmapLayerProps {
  points: HeatmapPoint[];
  radius?: number;
  blur?: number;
  max?: number;
  opacity?: number;
  gradient?: { [key: number]: string };
  interpolate?: boolean;
}

// Helper to render a horizontal color bar legend
export const HeatmapLegend: React.FC<{ min: number; max: number; gradient?: { [key: number]: string } }> = ({ min, max, gradient }) => {
  // Build a CSS linear-gradient from the gradient stops
  const stops = gradient
    ? Object.entries(gradient)
        .sort(([a], [b]) => parseFloat(a) - parseFloat(b))
        .map(([k, color]) => `${color} ${parseFloat(k) * 100}%`)
        .join(', ')
    : '';
  return (
    <div style={{
      position: 'fixed',
      right: 24,
      bottom: 24,
      background: 'rgba(255,255,255,0.95)',
      borderRadius: 8,
      boxShadow: '0 2px 8px #0002',
      padding: '12px 18px',
      zIndex: 3000,
      minWidth: 220
    }}>
      <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Population Heatmap</div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span style={{ fontSize: 12, marginRight: 8 }}>{min}</span>
        <div style={{
          flex: 1,
          height: 16,
          background: `linear-gradient(to right, ${stops})`,
          borderRadius: 4
        }} />
        <span style={{ fontSize: 12, marginLeft: 8 }}>{max}</span>
      </div>
    </div>
  );
};

const HeatmapLayer: React.FC<HeatmapLayerProps> = ({
  points,
  radius,
  blur,
  max,
  opacity = 0.7,
  gradient = {
    0.0: '#000004',
    0.2: '#420a68',
    0.4: '#932667',
    0.6: '#dd513a',
    0.8: '#fba50a',
    1.0: '#fcffa4'
  },
  interpolate = true
}) => {
  const map = useMap();
  const heatmapRef = useRef<any>(null);
  const [zoom, setZoom] = useState(map.getZoom());

  // Listen for zoom changes
  useMapEvents({
    zoomend: () => setZoom(map.getZoom()),
  });

  // Adaptive radius/blur based on zoom
  const adaptiveRadius = radius !== undefined ? radius : Math.max(10, Math.min(50, 20 + (zoom - 10) * 4));
  const adaptiveBlur = blur !== undefined ? blur : Math.max(10, Math.min(40, 15 + (zoom - 10) * 2));

  // Simple bilinear interpolation between points to add extra points for smoothing
  function interpolatePoints(points: HeatmapPoint[], gridSize = 4): HeatmapPoint[] {
    if (!interpolate || points.length < 4) return points;
    // Try to infer grid structure (assume regular grid)
    // Find unique lats and lngs
    const lats = Array.from(new Set(points.map(p => p.lat))).sort((a, b) => a - b);
    const lngs = Array.from(new Set(points.map(p => p.lng))).sort((a, b) => a - b);
    if (lats.length * lngs.length !== points.length) return points; // not a grid
    // Build a lookup
    const grid: { [key: string]: HeatmapPoint } = {};
    points.forEach(p => { grid[`${p.lat},${p.lng}`] = p; });
    const interpPoints: HeatmapPoint[] = [...points];
    for (let i = 0; i < lats.length - 1; i++) {
      for (let j = 0; j < lngs.length - 1; j++) {
        const p00 = grid[`${lats[i]},${lngs[j]}`];
        const p01 = grid[`${lats[i]},${lngs[j + 1]}`];
        const p10 = grid[`${lats[i + 1]},${lngs[j]}`];
        const p11 = grid[`${lats[i + 1]},${lngs[j + 1]}`];
        if (p00 && p01 && p10 && p11) {
          // Add interpolated points inside the cell
          for (let u = 1; u < gridSize; u++) {
            for (let v = 1; v < gridSize; v++) {
              const fu = u / gridSize;
              const fv = v / gridSize;
              // Bilinear interpolation
              const lat = lats[i] * (1 - fu) + lats[i + 1] * fu;
              const lng = lngs[j] * (1 - fv) + lngs[j + 1] * fv;
              const value =
                p00.value * (1 - fu) * (1 - fv) +
                p01.value * (1 - fu) * fv +
                p10.value * fu * (1 - fv) +
                p11.value * fu * fv;
              interpPoints.push({ lat, lng, value });
            }
          }
        }
      }
    }
    return interpPoints;
  }

  useEffect(() => {
    loggingService.debug('Rendering HeatmapLayer', { category: 'HeatmapLayer', pointCount: points.length });
    // Remove existing heatmap layer if it exists
    if (heatmapRef.current) {
      map.removeLayer(heatmapRef.current);
      heatmapRef.current = null;
    }

    // Optionally interpolate points for smoothing
    const displayPoints = interpolatePoints(points, 4); // 4x4 per cell for denser smoothing
    // Convert points to the format expected by leaflet.heat
    const heatmapData: [number, number, number][] = displayPoints.map(point => [
      point.lat,
      point.lng,
      point.value
    ] as [number, number, number]);

    // Create new heatmap layer
    if (heatmapData.length > 0) {
      const heatmapLayer = (L as any).heatLayer(heatmapData, {
        radius: adaptiveRadius,
        blur: adaptiveBlur,
        max: max || Math.max(...displayPoints.map(p => p.value)),
        gradient,
        opacity
      });

      heatmapLayer.addTo(map);
      heatmapRef.current = heatmapLayer;
    }

    // Cleanup function
    return () => {
      if (heatmapRef.current) {
        map.removeLayer(heatmapRef.current);
        heatmapRef.current = null;
      }
    };
  }, [map, points, adaptiveRadius, adaptiveBlur, max, gradient, opacity, interpolate]);

  // Find min/max for legend
  const minValue = points.length > 0 ? Math.min(...points.map(p => p.value)) : 0;
  const maxValue = points.length > 0 ? Math.max(...points.map(p => p.value)) : 1;

  return <>
    {/* This component doesn't render anything visible on the map itself */}
    <HeatmapLegend min={Math.floor(minValue)} max={Math.ceil(maxValue)} gradient={gradient} />
  </>;
};

export default HeatmapLayer; 